import isEmpty from 'lodash/isEmpty';

import { VARIANTS } from '@jobcase/react_pattern_library/components/Toaster/Toaster';

import { setNotification } from '../../notification/actions';
import { getCurrentEmployer } from '../../features/employers/selectors';
import { getProfileKey } from '../../authentication/selectors';

import JobsAPI from '../../../../utils/api/jobsAPI';
import SubOrgsAPI from '../../../../utils/api/subOrganizationsAPI';
import entity from './entity';

export const TYPES = {
  ...entity.getTypes(),
  CREATE_ORG_REQUEST: 'subOrganizations/CREATE_ORG_REQUEST',
  CREATE_ORG_SUCCESS: 'subOrganizations/CREATE_ORG_SUCCESS',
  CREATE_ORG_FAILURE: 'subOrganizations/CREATE_ORG_FAILURE',
  CREATE_SUB_ORG_REQUEST: 'subOrganizations/CREATE_SUB_ORG_REQUEST',
  CREATE_SUB_ORG_SUCCESS: 'subOrganizations/CREATE_SUB_ORG_SUCCESS',
  CREATE_SUB_ORG_FAILURE: 'subOrganizations/CREATE_SUB_ORG_FAILURE',
  CLEAR_NEW_SUB_ORG_ID: 'subOrganizations/CLEAR_NEW_SUB_ORG_ID',
  CLEAR_STATE: 'subOrganizations/CLEAR_STATE',
  GET_SUB_ORGS_REQUEST: 'subOrganizations/GET_SUB_ORGS_REQUEST',
  GET_SUB_ORGS_SUCCESS: 'subOrganizations/GET_SUB_ORGS_SUCCESS',
  GET_SUB_ORGS_FAILURE: 'subOrganizations/GET_SUB_ORGS_FAILURE',
  UPDATE_SUB_ORGS_REQUEST: 'subOrganizations/UPDATE_SUB_ORGS_REQUEST',
  UPDATE_SUB_ORGS_SUCCESS: 'subOrganizations/UPDATE_SUB_ORGS_SUCCESS',
  UPDATE_SUB_ORGS_FAILURE: 'subOrganizations/UPDATE_SUB_ORGS_FAILURE',
  UPDATE_SORT_OPTIONS: 'subOrganizations/UPDATE_SORT_OPTIONS',
  UPDATE_SEARCH_QUERY: 'subOrganizations/UPDATE_SEARCH_QUERY',
  DELETE_SUB_ORG_REQUEST: 'subOrganizations/DELETE_SUB_ORG_REQUEST',
  DELETE_SUB_ORG_SUCCESS: 'subOrganizations/DELETE_SUB_ORG_SUCCESS',
  DELETE_SUB_ORG_ERROR: 'subOrganizations/DELETE_SUB_ORG_ERROR',
  GET_PROFILES_BY_ORG_REQUEST: 'subOrganizations/GET_PROFILES_BY_ORG_REQUEST',
  GET_PROFILES_BY_ORG_SUCCESS: 'subOrganizations/GET_PROFILES_BY_ORG_SUCCESS',
  GET_PROFILES_BY_ORG_ERROR: 'subOrganizations/GET_PROFILES_BY_ORG_ERROR',
};

export const createOrganization = data => async (dispatch) => {
  dispatch({ type: TYPES.CREATE_ORG_REQUEST });
  try {
    const response = await entity.api.createOrganization(data);
    dispatch({ type: TYPES.CREATE_ORG_SUCCESS, payload: { response, newOrgId: response?.id } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.CREATE_ORG_FAILURE, payload: { error } });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while creating the organization. ${error.errors[0]}`, VARIANTS.INFO, null, true));
    } else {
      dispatch(setNotification('An error occurred while creating the organization.', VARIANTS.INFO, null, true));
    }
    throw error;
  }
};

export const updateOrganization = (orgId, data) => async (dispatch) => {
  dispatch({ type: TYPES.CREATE_ORG_REQUEST });
  try {
    const response = await entity.api.updateOrganization(orgId, data);
    dispatch({ type: TYPES.CREATE_ORG_SUCCESS, payload: { response, newOrgId: response?.id } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.CREATE_ORG_FAILURE, payload: { error } });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while updating the organization. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while updating the organization.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const createSubOrganization = data => async (dispatch, getState) => {
  dispatch({ type: TYPES.CREATE_SUB_ORG_REQUEST });
  try {
    const state = getState();
    const orgId = getCurrentEmployer(state);
    const response = await entity.api.createSubOrganization(data, orgId);
    const subOrgData = response?.data;
    dispatch({ type: TYPES.CREATE_SUB_ORG_SUCCESS, payload: { [subOrgData.id]: subOrgData, id: subOrgData.id } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.CREATE_SUB_ORG_FAILURE, payload: error });

    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while creating a sub organization. ${error.errors[0]}`, VARIANTS.INFO));
    } else if (!error?.message.includes('Zip code not found')) {
      dispatch(setNotification('An error occurred while creating a sub organization.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const updateSubOrganization = (data, subOrgId) => async (dispatch, getState) => {
  dispatch({ type: TYPES.UPDATE_SUB_ORGS_REQUEST });
  try {
    const state = getState();
    const orgId = getCurrentEmployer(state);
    const response = await entity.api.updateSubOrganization(data, orgId, subOrgId);
    const subOrgData = response?.data;
    dispatch({ type: TYPES.UPDATE_SUB_ORGS_SUCCESS, payload: { [subOrgData.id]: subOrgData } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.UPDATE_SUB_ORGS_FAILURE, payload: error });

    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while updating a sub organization. ${error.errors[0]}`, VARIANTS.INFO));
    } else if (!error?.message.includes('Zip code not found')) {
      dispatch(setNotification('An error occurred while updating a sub organization.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const getAllSubOrgs = options => async (dispatch, getState) => {
  dispatch({ type: TYPES.GET_SUB_ORGS_REQUEST, payload: { page: options?.page_index } });
  try {
    const state = getState();
    const organizationId = getCurrentEmployer(state);
    const response = await entity.api.getAllSubOrgs(organizationId, options);
    const { data: { content, number, totalElements, last } } = response;

    const query = content.map(({ id }) => `sub_org_id=${id}`);
    let countsResponse = null;
    let candidatesCountsResponse = null;
    if (!isEmpty(query)) {
      try {
        let response = await JobsAPI().getSubOrgJobCounts(query?.join('&'));
        countsResponse = response.content;
        response = await SubOrgsAPI().getSubOrgsCandidatesCount(organizationId);
        candidatesCountsResponse = response.data.content;
      } catch (error) {
        if (!isEmpty(error.errors)) {
          dispatch(setNotification(`An error occurred while loading sub-org jobs count. ${error.errors[0]}`, VARIANTS.INFO));
        } else if (+(error?.error_code) !== 404) {
          // 404 is expected for ' SubOrgsAPI().getSubOrgsCandidatesCount(organizationId)' response
          dispatch(setNotification('An error occurred while loading sub-org jobs count.', VARIANTS.INFO));
        }
      }
    }
    const data = content.reduce((result, entity) => {
      const counts = countsResponse?.find(({ id }) => id === entity.id);
      const candidateCount = candidatesCountsResponse?.find(({ id }) => id === entity.id);
      const entityCountData = { ...entity, ...counts, ...candidateCount };
      return { ...result, [entity.id]: entityCountData };
    }, {});
    dispatch({ type: TYPES.GET_SUB_ORGS_SUCCESS,
      payload: {
        content: data,
        sortOptions: { sort_field: options?.sort_field || 'created_at', desc_sort: options?.desc_sort || 'true' },
        pagination: { number, totalElements, last },
      },
    });
    return {
      data,
      pagination: { number, totalElements, last },
    };
  } catch (error) {
    dispatch({ type: TYPES.GET_SUB_ORGS_FAILURE, payload: error });
    if (error.error_code === 403) {
      return {};
    }
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading the sub orgs. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while loading the sub orgs.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const getSubOrganization = options => async (dispatch, getState) => {
  dispatch({ type: TYPES.GET_SUB_ORGS_REQUEST });
  try {
    const state = getState();
    const orgId = getCurrentEmployer(state);
    const profileKey = getProfileKey(state);
    const response = await entity.api.getSubOrganization({ ...options, orgId }, profileKey);
    const data = response?.data;
    const subOrgId = options?.subOrgId;
    dispatch({ type: TYPES.GET_REQUEST_SUCCESS, payload: { [subOrgId]: data } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.GET_REQUEST_ERROR, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading the sub organization. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while loading the sub organization.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const deleteSubOrganization = subOrgId => async (dispatch, getState) => {
  dispatch({ type: TYPES.DELETE_SUB_ORG_REQUEST });
  try {
    const state = getState();
    const orgId = getCurrentEmployer(state);
    const response = await entity.api.deleteSubOrganization(orgId, subOrgId);
    await dispatch({ type: TYPES.DELETE_SUB_ORG_SUCCESS, payload: { [subOrgId]: null } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.DELETE_SUB_ORG_ERROR, payload: { error } });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while deleting the sub organization. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while deleting the sub organization.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const clearState = () => (dispatch) => {
  dispatch({ type: TYPES.CLEAR_STATE });
};

export const updateSearchQuery = options => dispatch => dispatch({ type: TYPES.UPDATE_SEARCH_QUERY, payload: options });

export const updateSortOptions = options => dispatch => dispatch({ type: TYPES.UPDATE_SORT_OPTIONS, payload: options });

export const clearNewSubOrgId = () => ({ type: TYPES.CLEAR_NEW_SUB_ORG_ID });

export const getOrgProfiles = query => async (dispatch, getState) => {
  dispatch({ type: TYPES.GET_PROFILES_BY_ORG_REQUEST, payload: { page: query?.page } });
  try {
    const state = getState();
    const organizationId = getCurrentEmployer(state);
    const response = await entity.api.getOrgProfiles(organizationId, { page_size: 100, ...query });
    const { data: { content, number, totalElements, last } } = response;
    const data = content.reduce((result, entity) => ({ ...result, [entity.profile_id]: entity }), {});
    dispatch({ type: TYPES.GET_PROFILES_BY_ORG_SUCCESS, payload: { content: data, pagination: { number, totalElements, last } } });
  } catch (error) {
    dispatch({ type: TYPES.GET_PROFILES_BY_ORG_ERROR, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading the organization profiles. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while loading the organization profiles.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const getConversionPixel = subOrganizationId => async (dispatch, getState) => {
  try {
    const state = getState();
    const organizationId = getCurrentEmployer(state);
    const response = await entity.api.getConversionPixel(organizationId, subOrganizationId);
    return response;
  } catch (error) {
    if (+(error?.error_code) === 400 && error?.message) {
      dispatch(setNotification(`${error?.message}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while fetching conversion pixel.', VARIANTS.INFO));
    }

    throw error;
  }
};

export default {
  TYPES,
  ...entity.getActions(),
  createOrganization,
  createSubOrganization,
  updateSubOrganization,
  getSubOrganization,
  getAllSubOrgs,
  clearNewSubOrgId,
  clearState,
  updateOrganization,
  getConversionPixel,
};
